import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { BookOpen, Clock, ChevronRight, TrendingUp } from 'lucide-react';
import { blogArticles } from '../data/knowledgeHub';

const Blog = () => {
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const featuredArticle = blogArticles[0];
  const categories = [...new Set(blogArticles.map(a => a.category))];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-orange-50 via-white to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-3xl mx-auto text-center"
          >
            <BookOpen className="w-16 h-16 mx-auto mb-6" style={{ color: '#FF6B9D' }} />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              TypeIt4U Blog
            </h1>
            <p className="text-base sm:text-lg text-gray-600">
              Tips, guides, and insights on mobile typing, productivity, and AI keyboards
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Article */}
      <section className="py-12 bg-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <Link
              to={`/blog/${featuredArticle.slug}`}
              className="block bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 border border-orange-100 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5" style={{ color: '#FF6B9D' }} />
                <span className="text-sm font-medium text-orange-600">Featured</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: '#0A0A0A' }}>
                {featuredArticle.title}
              </h2>
              <p className="text-gray-600 mb-4">{featuredArticle.description}</p>
              <span className="inline-flex items-center gap-1 text-orange-600 font-medium">
                Read article <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-4 bg-white border-b border-gray-100">
        <div className="container">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700"
              >
                {category}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* All Articles */}
      <section className="py-16 bg-white">
        <div className="container">
          <h2 className="text-2xl font-bold mb-8" style={{ color: '#0A0A0A' }}>
            All Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.05 }}
              >
                <Link
                  to={`/blog/${article.slug}`}
                  className="block bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-orange-200 transition-all h-full"
                >
                  <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium mb-3">
                    {article.category}
                  </span>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: '#0A0A0A' }}>
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                    {article.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-sm text-orange-600 font-medium">
                    Read more <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="max-w-2xl mx-auto text-center"
          >
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Stay Updated
            </h3>
            <p className="text-gray-600 mb-6">
              Get the latest tips and updates from TypeIt4U
            </p>
            <a
              href="https://play.google.com/store/apps/details?id=com.typeit4u.keyboard"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                borderRadius: '26px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              Download TypeIt4U
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
